import { useTranslation } from "react-i18next";
import Avatar from "../../components/Avatar/Avatar";
import { fullName } from "../../utils/helpers/userDisplay";
import { formatShortDate } from "../../utils/helpers/dateDisplay";
import {
  FOCUS_RING,
  GROUP_EDGE,
  NUMBER_BTN,
  UNCOVERED_RAIL,
  ZERO_BTN,
} from "./coverageStyles";

// Whole days since a visit, for the "N days since the last one" line. Rounded
// from local midnight so a visit yesterday evening reads as 1, not 0.
export const daysSince = (iso) => {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const midnight = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((today - midnight) / 86400000));
};

// True when this row's teacher group is the school's actual gap: they can be
// observed and nobody has. Derived in one place because three surfaces draw it
// — the table's rail, the card's rail and the rose zero under both — and three
// copies of `x != null && x === 0` is three chances to write `!x` instead and
// paint the rail on an administrator who has no such figure at all.
export const isUncovered = (row) => row?.received_total === 0;

// `null` is not zero, and the difference matters more here than anywhere else
// on the page: the backend sends null for a role the person does not hold, and
// a 0 would accuse an administrator who has never taught of never having been
// visited. A zero is an accusation; this is an absence, and it says so in a
// word — the row now carries TWO em dashes, "does not apply" and "never
// happened", and they must not read alike.
function NotApplicable() {
  const { t } = useTranslation();
  return (
    <>
      <span className="text-base font-black text-slate-300">—</span>
      <span className="mt-0.5 block text-[10px] font-semibold text-slate-400">
        {t("coverage.col.notApplicableShort")}
      </span>
    </>
  );
}

// Where the deleted «ПРОСРОЧЕНО / ЗАКРЫТО» column went: a line inside the count
// cell it belongs to, and only when there is something to say. That column
// spent a sixth of the table's width restating zero, and it could only ever
// show one side's backlog while the header said neither.
function OverdueLine({ count }) {
  const { t } = useTranslation();
  if (!count) return null;
  return (
    <span className="mt-1 flex items-center gap-1 text-[10px] font-bold text-amber-700">
      <svg
        className="h-3 w-3 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
        />
      </svg>
      {t("coverage.col.overdueShort", { count })}
    </span>
  );
}

// One count, its split and its backlog. `uncovered` is passed rather than
// re-derived because only the TEACHER group may go rose: a zero in «провёл» is
// a neutral slate zero on purpose.
//
// A null count never hides a ⚠ line, and that is an invariant, not luck: the
// backend counts an outstanding booking as evidence of the capability, so a
// person with anything overdue always has a real number here.
export function CountCell({
  value,
  official,
  peer,
  overdue,
  uncovered = false,
  onOpen,
  ariaLabel,
  align = "right",
}) {
  const { t } = useTranslation();
  const box = align === "right" ? "flex flex-col items-end" : "flex flex-col items-start";

  if (value == null) {
    return (
      <span className={box}>
        <NotApplicable />
      </span>
    );
  }

  return (
    <span className={box}>
      {/* A zero opens the drawer too. It is the cell most worth asking about —
          "nobody visited them" is exactly when you want to know whether a visit
          is at least BOOKED — and the drawer already answers it: its second
          section lists the scheduled ones, so a zero opens on an empty
          "conducted" list above a live queue rather than on nothing. `null` is
          the one case that stays inert: there is no such role to look into. */}
      <button
        type="button"
        onClick={onOpen}
        aria-label={ariaLabel}
        className={`text-base ${value > 0 ? NUMBER_BTN : ZERO_BTN(uncovered)}`}
      >
        {value}
      </button>

      {/* The official/peer split earns its line only under a real count.
          «офиц. 0 · взаим. 0» under a 0 states the same nothing three times. */}
      {value > 0 && (
        <span className="mt-0.5 text-[10px] font-semibold tabular-nums text-slate-400">
          {t("coverage.col.splitShort", { official: official ?? 0, peer: peer ?? 0 })}
        </span>
      )}

      {/* The word under the rose zero. The rail is a second signal, never the
          first: this line is what survives greyscale, a projector and a viewer
          who cannot tell rose from slate. */}
      {uncovered && (
        <span className="mt-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-600">
          {t("coverage.col.neverVisited")}
        </span>
      )}

      <OverdueLine count={overdue} />
    </span>
  );
}

export function LastVisitCell({ at }) {
  const { t } = useTranslation();
  if (!at) {
    return (
      <span className="text-slate-300" title={t("coverage.col.noVisitsYet")}>
        —
      </span>
    );
  }
  const gap = daysSince(at);
  return (
    <>
      <span className="text-sm font-semibold text-slate-700">{formatShortDate(at)}</span>
      {gap != null && (
        <span className="mt-0.5 block truncate text-[10px] font-semibold text-slate-400">
          {t("schedule.daysSinceLast", { count: gap })}
        </span>
      )}
    </>
  );
}

export function personMeta(person) {
  return [person?.department, (person?.subjects || []).map((s) => s.name).join(", ")]
    .filter(Boolean)
    .join(" · ");
}

export function PersonIdentity({ person, onOpen, avatarClass, nameClass }) {
  const meta = personMeta(person);
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`-m-1 flex max-w-full items-center gap-3 rounded-xl p-1 text-left ${FOCUS_RING}`}
    >
      <Avatar
        user={person}
        className={`shrink-0 rounded-xl bg-slate-100 font-black text-slate-500 ${avatarClass}`}
        alt=""
      />
      <span className="min-w-0">
        <span className={`block truncate font-bold text-slate-900 ${nameClass}`}>
          {fullName(person)}
        </span>
        {meta && (
          <span className="block truncate text-xs font-medium text-slate-500">{meta}</span>
        )}
      </span>
    </button>
  );
}

// `aria-sort` belongs only on a column this table can actually be ordered by,
// and after the merge that is the name column alone. «Последний» appears twice
// and `ordering=last_activity` is neither of them — it is the LATER of the two
// dates, so marking either column as the sorted one would be a lie an assistive
// reader has no way to check. That ordering lives in the sort control above the
// table, where it can say so in words.
//
// There is also deliberately no ordering that sorts volume descending — that is
// a leaderboard of colleagues, and this page does not publish one.
function SortableTh({ label, active, direction, onClick, rowSpan, className = "" }) {
  return (
    <th
      scope="col"
      rowSpan={rowSpan}
      aria-sort={active ? direction : "none"}
      className={`px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500 ${className}`}
    >
      <button
        type="button"
        onClick={onClick}
        className={`-m-1 inline-flex items-center gap-1 rounded-md p-1 transition hover:text-slate-800 ${FOCUS_RING} ${
          active ? "text-indigo-700" : ""
        }`}
      >
        {label}
        <svg
          className={`h-3 w-3 ${active ? "" : "opacity-0"}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d={direction === "ascending" ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
          />
        </svg>
      </button>
    </th>
  );
}

const TH = "px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500";
// The two header rows carry their own padding rather than composing `TH` with
// an override: `pt-0` after `py-3` resolves by the order Tailwind emits the
// padding utilities, not by the order they are written, which is a rule that
// holds until it does not.
const GROUP_TH =
  "px-4 pt-3 pb-1 text-left text-[10px] font-black uppercase tracking-widest text-slate-600";
const METRIC_TH =
  "px-4 pb-3 pt-0 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500";

// ONE table, one row per person, two labelled role groups side by side. It was
// two tabs over the same eleven people, where switching changed the meaning of
// the «ПОСЛЕДНИЙ» heading, which metric fed the chip and the sort — same rows,
// same numbers, both badges reading 11. Two real date columns are what bought
// the deletion of the tab strip.
//
// `sortable` is off for the guests table: it is a second, separately-queried
// list, and a header click there would silently reorder the roster above it.
export default function PersonTable({
  rows,
  caption,
  ordering,
  onOrdering,
  onOpenVisits,
  sortable = true,
  flagUncovered = true,
}) {
  const { t } = useTranslation();

  return (
    // `relative` exists for the caption below and must not be removed with it.
    // `sr-only` is `position: absolute`, and nothing up this tree is positioned
    // — so the caption's containing block became the BODY. It then sat at
    // y=954 on a 900px viewport: a 1×1 invisible box outside the app shell's
    // clipped column, stretching the document by 54px and giving the whole
    // window a second scrollbar that slid the shell under the page. Measured:
    // window.scrollY reached 55 on /coverage and 0 on every other route.
    // Positioning the table keeps the caption inside <main>, where it is
    // clipped like everything else.
    <table className="relative w-full border-collapse">
      {caption && <caption className="sr-only">{caption}</caption>}

      {/* Real column groups, not decoration: `scope="colgroup"` is defined
          against the table's COLUMN GROUPS, and with no <colgroup> in the
          document the group header associates with nothing — leaving the two
          identically-named «ПОСЛЕДНИЙ» columns indistinguishable to exactly the
          readers who cannot see that they sit under different banners. */}
      <colgroup>
        <col />
      </colgroup>
      <colgroup span={2} />
      <colgroup span={2} />

      {/* NOT sticky, and that is deliberate rather than forgotten. The table's
          wrapper is `overflow-x-auto`, which makes the element a scrollport on
          BOTH axes (CSS cannot leave one axis visible when the other is not),
          so a sticky thead pins to that wrapper — which scrolls away with the
          page — and not to <main>. Measured: it sat at y=45 against a main
          starting at y=65, i.e. already gone. Making it work means a nested
          vertical scroll region inside a page that already scrolls, which is
          worse than what it fixes. */}
      <thead className="border-b border-slate-200 bg-slate-50/60">
        <tr>
          {/* Bottom-aligned across both header rows so «СОТРУДНИК» sits on the
              metric row's baseline rather than floating up beside the group
              banners, which name the columns to its right and not this one. */}
          {sortable ? (
            <SortableTh
              rowSpan={2}
              className="align-bottom"
              label={t("coverage.col.person")}
              active={ordering === "name"}
              direction="ascending"
              onClick={() => onOrdering("name")}
            />
          ) : (
            <th scope="col" rowSpan={2} className={`${TH} align-bottom`}>
              {t("coverage.col.person")}
            </th>
          )}
          <th scope="colgroup" colSpan={2} className={`${GROUP_TH} ${GROUP_EDGE}`}>
            {t("coverage.group.observer")}
          </th>
          <th scope="colgroup" colSpan={2} className={`${GROUP_TH} ${GROUP_EDGE}`}>
            {t("coverage.group.teacher")}
          </th>
        </tr>
        <tr>
          <th scope="col" className={`${METRIC_TH} ${GROUP_EDGE} text-right`}>
            {t("coverage.col.conducted")}
          </th>
          <th scope="col" className={METRIC_TH}>
            {t("coverage.col.lastVisit")}
          </th>
          <th scope="col" className={`${METRIC_TH} ${GROUP_EDGE} text-right`}>
            {t("coverage.col.visited")}
          </th>
          <th scope="col" className={METRIC_TH}>
            {t("coverage.col.lastVisit")}
          </th>
        </tr>
      </thead>

      <tbody className="divide-y divide-slate-100">
        {rows.map((row) => {
          const person = row.person || {};
          const uncovered = flagUncovered && isUncovered(row);
          const name = fullName(person);
          return (
            // The <tr> is not clickable: a row that is one big button swallows
            // the buttons inside it and gives the keyboard one target where the
            // eye sees five.
            <tr key={person.uuid} className="align-top hover:bg-slate-50/60">
              <th scope="row" className="px-4 py-3 text-left font-normal">
                <PersonIdentity
                  person={person}
                  onOpen={() => onOpenVisits(row)}
                  avatarClass="h-9 w-9 text-[11px]"
                  nameClass="text-sm hover:text-indigo-700"
                />
              </th>

              <td className={`${GROUP_EDGE} px-4 py-4`}>
                <CountCell
                  value={row.conducted_total}
                  official={row.conducted_official}
                  peer={row.conducted_peer}
                  overdue={row.conducted_overdue}
                  onOpen={() => onOpenVisits(row, "conducted")}
                  ariaLabel={t("coverage.col.conductedAria", { name })}
                />
              </td>

              <td className="max-w-[10rem] px-4 py-4">
                <LastVisitCell at={row.last_conducted_at} />
              </td>

              {/* The rail is a property of THIS cell, not of the row: it says
                  nobody has visited this teacher, and it may never be read as a
                  verdict on the observer group beside it. */}
              <td className={`relative ${GROUP_EDGE} px-4 py-4`}>
                {uncovered && (
                  <span
                    aria-hidden="true"
                    className={`absolute left-0 top-0 h-full w-[3px] ${UNCOVERED_RAIL}`}
                  />
                )}
                <CountCell
                  value={row.received_total}
                  official={row.received_official}
                  peer={row.received_peer}
                  overdue={row.received_overdue}
                  uncovered={uncovered}
                  onOpen={() => onOpenVisits(row, "received")}
                  ariaLabel={t("coverage.col.receivedAria", { name })}
                />
              </td>

              <td className="max-w-[10rem] px-4 py-4">
                <LastVisitCell at={row.last_received_at} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
